import { AppDispatch } from '../../redux/store';
import { brand } from '../../theme/themePrimitives';
import { fetchPipelineTemplatesThunk } from '../../redux/thunks/pipeline';
import ImageClass from '../../assets';
import { useDispatch } from 'react-redux';
import { Avatar, Box, Button, Typography } from '@mui/material';

/**
 * @heading1: RenderEmptyState Component
 * @paragraph: This component displays a message and UI when there are no pipelines or stages available.
 * It encourages users to add a new pipeline or stage and provides a button for the action.
 * @interface: RenderEmptyStateProps
 * @property: {boolean} isPipeline - Determines if the empty state is for pipelines or stages.
 * @property: {() => void} onSubmit - Function triggered when the action button is clicked.
 * @property: {boolean} disabled - Disables the action button when true.
 */
interface RenderEmptyStateProps {
    isPipeline: boolean;
    onSubmit: () => void;
    disabled: boolean;
}

/**
 * @heading2: RenderEmptyState
 * @parargraph: Renders an empty state message with an action button for adding a pipeline or stage.
 * @param: {RenderEmptyStateProps} props - The props for the component.
 * @returns: JSX.Element - The rendered empty state UI.
 */
const RenderEmptyState: React.FC<RenderEmptyStateProps> = ({ isPipeline = false, onSubmit, disabled }: RenderEmptyStateProps) => {
    const dispatch = useDispatch<AppDispatch>();

    const onSubmitHandler = () => {
        if (isPipeline) {
            dispatch(fetchPipelineTemplatesThunk()).unwrap();
        }
        onSubmit();
    };
    return (
        <Box width="100%" display="flex" flexDirection="column" justifyContent="center" alignItems="center" gap="10px">
            {/* Placeholder image for the empty state */}
            <Avatar
                sx={{ width: 'auto', height: 285, bgcolor: 'white' }}
                src={ImageClass.emptyPipeline}
                variant="square"
            />
            {/* Title indicating no pipelines or stages available */}
            <Typography fontSize="20px" variant="body1" fontWeight="600" color={brand[300]}>
                No {isPipeline ? 'Pipeline' : 'Stage'} Available
            </Typography>
            {/* Description providing instructions for the user */}
            <Typography fontSize="16px" variant="body1" fontWeight="400" color="rgba(111,118,126,1)">
                {isPipeline ? 'Add a new pipeline by clicking below' : 'Add a new stage to your pipeline by clicking below'}
            </Typography>
            {/* Action button for adding a pipeline or stage */}
            <Button variant="contained" onClick={onSubmitHandler} disabled={disabled}>
                Add a {isPipeline ? 'pipeline' : 'stage'}
            </Button>
        </Box>);
};

export default RenderEmptyState;